import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Search, User, CalendarClock, Building2, X } from "lucide-react";

export function DashboardSearch() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const box = useRef(null);
  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return; }
    const t = setTimeout(() => api.get("/search", { params: { q } }).then((r) => { setRes(r.data); setOpen(true); }).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    const onDoc = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const go = (path) => { setOpen(false); nav(path); };
  const empty = res && !res.contacts.length && !res.bookings.length && !res.properties.length;
  return (
    <div ref={box} className="relative" data-testid="dashboard-search">
      <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 h-12 focus-within:ring-2 focus-within:ring-teal-600/30 transition-shadow">
        <Search className="w-4 h-4 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => res && setOpen(true)}
          onKeyDown={(e) => e.key === "Escape" && setOpen(false)}
          placeholder="Search guests, bookings, properties, contractors…" data-testid="dashboard-search-input"
          className="flex-1 bg-transparent outline-none text-sm placeholder:text-slate-400" />
        {q && <button onClick={() => { setQ(""); setRes(null); }} data-testid="dashboard-search-clear" className="text-slate-400 hover:text-slate-700 transition-colors"><X className="w-4 h-4" /></button>}
      </div>
      {open && res && (
        <div className="absolute z-40 mt-2 w-full rounded-2xl border border-slate-100 bg-white shadow-xl p-2 max-h-[420px] overflow-y-auto" data-testid="dashboard-search-results">
          {empty && <div className="p-4 text-sm text-slate-400" data-testid="dashboard-search-empty">No matches for “{q}”</div>}
          <Group title="Contacts" items={res.contacts} render={(c) => (
            <Row key={c.id} icon={User} testId={`dashboard-search-contact-${c.id}`} onClick={() => go(`/contacts/${c.id}`)}
              title={`${c.first_name || ""} ${c.last_name || ""}`.trim() || c.company || c.email}
              sub={[c.type, c.email, c.phone].filter(Boolean).join(" · ")} />
          )} />
          <Group title="Bookings" items={res.bookings} render={(b) => (
            <Row key={b.id} icon={CalendarClock} testId={`dashboard-search-booking-${b.id}`} onClick={() => go(`/contacts/${b.contact_id}`)}
              title={`${b.guest_name} · ${(b.check_in || "").slice(0, 10)} → ${(b.check_out || "").slice(0, 10)}`}
              sub={[(b.property_name || "").replace(/\*/g, " · "), b.channel, b.status].filter(Boolean).join(" · ")} />
          )} />
          <Group title="Properties" items={res.properties} render={(p) => (
            <Row key={p.id} icon={Building2} testId={`dashboard-search-property-${p.id}`} onClick={() => go(`/properties?edit=${p.id}`)}
              title={(p.name || "").replace(/\*/g, " · ")} sub={p.address} />
          )} />
        </div>
      )}
    </div>
  );
}

const Group = ({ title, items, render }) => items.length ? (
  <div className="mb-1"><div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-slate-400">{title}</div>{items.map(render)}</div>
) : null;

const Row = ({ icon: Icon, title, sub, onClick, testId }) => (
  <button onClick={onClick} data-testid={testId} className="w-full flex items-start gap-3 rounded-xl px-3 py-2 text-left hover:bg-teal-50/60 transition-colors">
    <Icon className="w-4 h-4 text-teal-700 mt-0.5 shrink-0" />
    <div className="min-w-0"><div className="text-sm text-slate-800 truncate">{title}</div>{sub && <div className="text-[11px] text-slate-500 truncate">{sub}</div>}</div>
  </button>
);
